import { useActiveTenant } from "features/tenant-identity";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { FeatureFlag } from "./featureFlags";
import { useEffectiveFeatureFlags } from "./useEffectiveFeatureFlags";

const STORAGE_KEY_PREFIX = "scs.featureFlags.";
const DEFAULT_REFRESH_SECONDS = 60;
const MAX_REFRESH_SECONDS = 300;

type FeatureFlagMap = Record<string, boolean>;

interface FeatureFlagsContextValue {
  /** True until a set has been established, either from the server or from the persisted snapshot. */
  loading: boolean;
  /** Only an explicit `true` licenses a capability: an absent key is treated as not licensed. */
  isEnabled: (key: FeatureFlag) => boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  loading: true,
  isEnabled: () => false
});

const storageKey = (tenant: string) => `${STORAGE_KEY_PREFIX}${tenant}`;

const readPersisted = (
  tenant: string | undefined
): FeatureFlagMap | undefined => {
  if (!tenant) return undefined;

  try {
    const raw = window.localStorage.getItem(storageKey(tenant));
    return raw ? (JSON.parse(raw) as FeatureFlagMap) : undefined;
  } catch {
    // A corrupt or unavailable store must never block sign-in; fall back to the conservative shell.
    return undefined;
  }
};

const persist = (tenant: string | undefined, flags: FeatureFlagMap) => {
  if (!tenant) return;

  try {
    window.localStorage.setItem(storageKey(tenant), JSON.stringify(flags));
  } catch {
    // Persistence is an optimisation, never a requirement.
  }
};

interface FeatureFlagsProviderProps {
  /**
   *  Tenant the flags belong to. Defaults to the signed-in user's active tenant; a persisted set from
   *  a different tenant is never applied.
   */
  tenant?: string;
  children: React.ReactNode;
}

/**
 * Supplies the tenant's licensed capabilities to the app.
 *
 * Never renders optimistically: until a set is known, flagged UI stays hidden so an unlicensed entry
 * point is not briefly visible. Resolution never blocks startup — unflagged parts of the app render
 * regardless.
 */
export default function FeatureFlagsProvider({
  tenant,
  children
}: FeatureFlagsProviderProps) {
  const activeTenant = useActiveTenant();
  const scope = tenant ?? activeTenant;

  const [flags, setFlags] = useState<FeatureFlagMap | undefined>(() =>
    readPersisted(scope)
  );
  const { data, refetch, startPolling, stopPolling } =
    useEffectiveFeatureFlags();
  const maxAgeSeconds = data?.features?.effective?.maxAgeSeconds;
  const refreshSeconds =
    typeof maxAgeSeconds === "number" &&
    Number.isFinite(maxAgeSeconds) &&
    maxAgeSeconds > 0
      ? Math.min(maxAgeSeconds, MAX_REFRESH_SECONDS)
      : DEFAULT_REFRESH_SECONDS;

  // A snapshot belonging to another tenant is discarded; fresh flags are fetched for the new scope.
  useEffect(() => {
    setFlags(readPersisted(scope));
    if (scope) {
      void refetch().catch(() => undefined);
    }
  }, [scope, refetch]);

  useEffect(() => {
    const effective = data?.features?.effective;
    if (!effective) return;

    const resolved: FeatureFlagMap = {};
    effective.flags.forEach(flag => {
      resolved[flag.key] = flag.enabled;
    });

    setFlags(resolved);
    persist(scope, resolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]); // scope intentionally excluded: applying stale data on scope change would re-show the previous tenant's flags

  useEffect(() => {
    if (!scope) return;

    startPolling(refreshSeconds * 1000);
    return () => stopPolling();
  }, [scope, refreshSeconds, startPolling, stopPolling]);

  const isEnabled = useCallback(
    (key: FeatureFlag) => (flags ? flags[key] === true : false),
    [flags]
  );

  const value = useMemo<FeatureFlagsContextValue>(
    () => ({ loading: flags === undefined, isEnabled }),
    [flags, isEnabled]
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export const useFeatureFlags = () => useContext(FeatureFlagsContext);
